// Dev-only fixtures served when REACT_APP_VISIBILITY_MOCK=true — localhost has no
// DataForSEO credentials, so the real endpoints are gated off and never return data.

const RESPONSE_DELAY_MS = 400;

const GRID_SIZE = 5;
const CENTER = { lat: 52.2297, lng: 21.0122 };
const LAT_STEP = 0.008983;
const LNG_STEP = 0.014671;

const KEYWORDS = [
    { keywordId: 'mock-kw-1', text: 'car rental warsaw', spread: 0 },
    { keywordId: 'mock-kw-2', text: 'rent a car', spread: 1 },
    { keywordId: 'mock-kw-3', text: 'car hire near me', spread: 2 },
    { keywordId: 'mock-kw-4', text: 'luxury car rental', spread: 3 },
    { keywordId: 'mock-kw-5', text: 'cheap car rental', spread: 5 },
];

const COMPETITOR_NAMES = [
    'Panek CarSharing',
    'Express Rent a Car',
    'Warsaw Car Hire Co.',
    'AutoFlota Premium',
    'City Wheels Rental',
    'Vistula Motors',
];

const LOCATIONS = [
    { locationId: 9001, name: 'Warsaw — City Centre', city: 'Warsaw', base: 1, delta90d: -3.1, autoScan: true },
    { locationId: 9002, name: 'Warsaw — Airport', city: 'Warsaw', base: 2, delta90d: -1.2, autoScan: true },
    { locationId: 9003, name: 'Kraków — Old Town', city: 'Kraków', base: 3, delta90d: 0, autoScan: false },
    { locationId: 9004, name: 'Wrocław — Main Square', city: 'Wrocław', base: 6, delta90d: 2.4, autoScan: true },
    { locationId: 9005, name: 'Gdańsk — Waterfront', city: 'Gdańsk', base: 10, delta90d: -0.6 },
    {
        locationId: 9006,
        name: 'Poznań — Stary Rynek',
        city: 'Poznań',
        base: 14,
        delta90d: 1.8,
        scanInProgress: true,
        autoScan: true,
    },
    { locationId: 9007, name: 'Łódź — Piotrkowska', city: 'Łódź', base: null },
];

const delayed = value => new Promise(resolve => setTimeout(() => resolve(value), RESPONSE_DELAY_MS));

const autoScanFlags = location => {
    const stored = settingsByLocation.get(location.locationId);
    return {
        isConfigured: stored != null || location.autoScan != null,
        autoScanEnabled: stored != null ? stored.rescanMonthly : !!location.autoScan,
    };
};

const cellRank = (row, col, base, spread) => {
    const distance = Math.abs(row - 2) + Math.abs(col - 2);
    const rank = base + distance * spread;
    return rank > 20 ? null : rank;
};

const buildGrid = (base, spread) => {
    const cells = [];
    for (let row = 0; row < GRID_SIZE; row++) {
        for (let col = 0; col < GRID_SIZE; col++) {
            const rank = cellRank(row, col, base, spread);
            cells.push({
                row,
                col,
                rank,
                latitude: CENTER.lat + (row - 2) * LAT_STEP,
                longitude: CENTER.lng + (col - 2) * LNG_STEP,
                topCompetitor: rank === 1 ? null : COMPETITOR_NAMES[(row + col) % COMPETITOR_NAMES.length],
            });
        }
    }
    return cells;
};

const buildKeywords = base =>
    KEYWORDS.map(keyword => {
        const grid = buildGrid(base, keyword.spread);
        const ranks = grid.map(cell => cell.rank ?? 21);
        const top3Count = ranks.filter(rank => rank <= 3).length;
        return {
            keywordId: keyword.keywordId,
            text: keyword.text,
            avgRank: Math.round((ranks.reduce((sum, rank) => sum + rank, 0) / ranks.length) * 10) / 10,
            visibilityScore: Math.round((top3Count / ranks.length) * 10000) / 10000,
            top3Count,
            grid,
        };
    });

const buildRow = location => {
    if (location.base == null) {
        return {
            locationId: location.locationId,
            name: location.name,
            city: location.city,
            visibilityScore: null,
            avgRank: null,
            trend: Array(12).fill(null),
            delta90d: null,
            lastScannedAt: null,
            ...autoScanFlags(location),
        };
    }

    const keywords = buildKeywords(location.base);
    const avgRank =
        Math.round((keywords.reduce((sum, keyword) => sum + keyword.avgRank, 0) / keywords.length) * 10) / 10;
    const visibilityScore =
        Math.round((keywords.reduce((sum, keyword) => sum + keyword.visibilityScore, 0) / keywords.length) * 10000) /
        10000;
    return {
        locationId: location.locationId,
        name: location.name,
        city: location.city,
        visibilityScore,
        avgRank,
        trend: [
            null,
            avgRank + 3.2,
            avgRank + 2.1,
            null,
            avgRank + 2.6,
            avgRank + 1.9,
            avgRank + 1.4,
            null,
            avgRank + 1.1,
            avgRank + 0.7,
            avgRank + 0.3,
            avgRank,
        ],
        delta90d: location.delta90d,
        lastScannedAt: new Date(Date.now() - location.base * 86400000).toISOString(),
        ...autoScanFlags(location),
    };
};

const SCAN_HISTORY = 3;

const buildScans = location =>
    location.base == null
        ? []
        : Array.from({ length: SCAN_HISTORY }, (_, index) => ({
              id: `mock-scan-${location.locationId}-${index}`,
              completedAt: new Date(Date.now() - (location.base + index * 30) * 86400000).toISOString(),
          }));

const buildDetail = (location, scanIndex = 0) => {
    const scans = buildScans(location);
    const scanLocation = location.base == null ? location : { ...location, base: location.base + scanIndex * 2 };
    const row = buildRow(scanLocation);
    const keywords = scanLocation.base == null ? [] : buildKeywords(scanLocation.base);
    return {
        locationId: location.locationId,
        name: location.name,
        scanId: scans[scanIndex]?.id ?? null,
        scans,
        visibilityScore: row.visibilityScore,
        avgRank: row.avgRank,
        top3Points: keywords.reduce((sum, keyword) => sum + keyword.top3Count, 0),
        totalPoints: keywords.length * GRID_SIZE * GRID_SIZE,
        gridSize: GRID_SIZE,
        gridSpacingMeters: 1000,
        lastScannedAt: row.lastScannedAt,
        scanInProgress: !!location.scanInProgress,
        canRescan: true,
        centerLatitude: CENTER.lat,
        centerLongitude: CENTER.lng,
        keywords,
        competitors:
            location.base == null
                ? []
                : COMPETITOR_NAMES.map((title, index) => ({
                      placeId: `mock-place-${index}`,
                      title,
                      topThreeShare: Math.round((0.9 - index * 0.13) * 10000) / 10000,
                      rating: Math.round((4.9 - index * 0.2) * 10) / 10,
                      votes: 480 - index * 60,
                      velocityPerMonth: index % 2 === 0 ? Math.round((14 - index * 1.5) * 10) / 10 : null,
                      isYou: false,
                  })).concat([
                      {
                          placeId: 'mock-place-you',
                          title: location.name,
                          topThreeShare: row.visibilityScore,
                          rating: 4.2,
                          votes: 210,
                          velocityPerMonth: 6.5,
                          isYou: true,
                      },
                  ]),
    };
};

function getVisibilityStatus() {
    return delayed({ enabled: true });
}

function getVisibilitySummary() {
    const rows = LOCATIONS.map(buildRow);
    const withData = rows.filter(row => row.visibilityScore != null);
    return delayed({
        avgVisibility:
            Math.round((withData.reduce((sum, row) => sum + row.visibilityScore, 0) / withData.length) * 10000) / 10000,
        avgVisibilityDelta: 0.04,
        locationsTop3: withData.filter(row => row.avgRank <= 3).length,
        locationsTotal: rows.length,
        avgRank: Math.round((withData.reduce((sum, row) => sum + row.avgRank, 0) / withData.length) * 10) / 10,
        avgRankDelta: -1.8,
    });
}

function getVisibilityLocations(data, { sort, dir, page, size }) {
    const rows = LOCATIONS.map(buildRow);
    const key = { avgrank: 'avgRank', delta90d: 'delta90d' }[String(sort).toLowerCase()] || 'visibilityScore';
    const factor = dir === 'asc' ? 1 : -1;
    const withData = rows
        .filter(row => row[key] != null)
        .sort((a, b) => (a[key] - b[key]) * factor || a.avgRank - b.avgRank);
    const ordered = withData.concat(rows.filter(row => row[key] == null));
    return delayed({
        items: ordered.slice(page * size, page * size + size),
        totalCount: ordered.length,
        pages: Math.ceil(ordered.length / size),
        page,
        pageSize: size,
    });
}

function getVisibilityLocation(id, scanId) {
    const location = LOCATIONS.find(item => item.locationId === Number(id));
    const scanIndex = scanId ? Number(String(scanId).split('-').pop()) || 0 : 0;
    return delayed(location ? buildDetail(location, scanIndex) : null);
}

function getVisibilityCompetitor(locationId, placeId, scanId) {
    const location = LOCATIONS.find(item => item.locationId === Number(locationId));
    if (!location || location.base == null) return delayed(null);

    const index = Number(String(placeId).split('-').pop()) || 0;
    const scanIndex = scanId ? Number(String(scanId).split('-').pop()) || 0 : 0;
    const base = Math.min(location.base + index + scanIndex * 2, 18);
    const keywords = buildKeywords(base);
    return delayed({
        placeId,
        title: COMPETITOR_NAMES[index % COMPETITOR_NAMES.length],
        scanId: scanId ?? buildScans(location)[0]?.id,
        topThreeShare: Math.round((0.9 - index * 0.13) * 10000) / 10000,
        rating: Math.round((4.9 - index * 0.2) * 10) / 10,
        votes: 480 - index * 60,
        velocityPerMonth: index % 2 === 0 ? Math.round((14 - index * 1.5) * 10) / 10 : null,
        place: {
            address: `${10 + index} Marszałkowska St`,
            city: 'Warsaw',
            phone: `+48 22 555 01${String(index).padStart(2, '0')}`,
            website: index % 3 === 2 ? null : `https://competitor-${index}.example.com`,
            latitude: CENTER.lat + 0.004 + index * 0.002,
            longitude: CENTER.lng - 0.006 + index * 0.003,
            googleMapsUrl: `https://maps.google.com/?cid=90000${index}`,
            primaryCategory: 'Car rental agency',
            categories: ['Van rental agency', 'Transportation service'],
        },
        keywords,
    });
}

function rescanVisibilityLocation() {
    return delayed({ ok: true });
}

const DEFAULT_SETTINGS = {
    gridSize: 5,
    gridSpacingMeters: 1000,
    rescanMonthly: false,
    keywordsUserManaged: false,
    excludedCells: [],
    keywords: KEYWORDS.map(keyword => keyword.text),
};

const settingsByLocation = new Map();

const toSettingsModel = stored => ({
    gridSize: stored.gridSize,
    gridSpacingMeters: stored.gridSpacingMeters,
    keywordsUserManaged: stored.keywordsUserManaged,
    keywords: stored.keywords,
    maxKeywords: 5,
    gridSizeOptions: [3, 5, 7],
    gridSpacingOptions: [250, 500, 750, 1000],
    excludedCells: stored.excludedCells,
    centerLatitude: CENTER.lat,
    centerLongitude: CENTER.lng,
});

// Every reader must share this seed-data fallback, or a save appears to silently flip the
// switch on a location whose cadence was never stored.
const storedSettings = id => {
    const location = LOCATIONS.find(item => item.locationId === Number(id));
    return (
        settingsByLocation.get(Number(id)) || {
            ...DEFAULT_SETTINGS,
            rescanMonthly: location?.autoScan === true,
        }
    );
};

function getVisibilitySettings(id) {
    return delayed(toSettingsModel(storedSettings(id)));
}

function updateVisibilitySettings(id, data) {
    const firstScanStarted = !settingsByLocation.has(Number(id));
    const current = storedSettings(id);
    const keywords = (data.keywords || []).map(text => text.trim().toLowerCase()).filter(Boolean);
    const stored = {
        gridSize: data.gridSize,
        gridSpacingMeters: data.gridSpacingMeters,
        rescanMonthly: current.rescanMonthly === true,
        excludedCells: data.excludedCells || [],
        keywords,
        keywordsUserManaged:
            current.keywordsUserManaged ||
            [...keywords].sort().join('\n') !==
                current.keywords
                    .map(text => text.toLowerCase())
                    .sort()
                    .join('\n'),
    };
    settingsByLocation.set(Number(id), stored);
    return delayed({ ...toSettingsModel(stored), firstScanStarted });
}

function setVisibilityAutoScan(id, enabled) {
    settingsByLocation.set(Number(id), { ...storedSettings(id), rescanMonthly: enabled === true });
    return delayed({ ok: true });
}

export const visibilityMockService = {
    getVisibilityStatus,
    getVisibilitySummary,
    getVisibilityLocations,
    getVisibilityLocation,
    getVisibilityCompetitor,
    getVisibilitySettings,
    updateVisibilitySettings,
    setVisibilityAutoScan,
    rescanVisibilityLocation,
};
